'use client';

import { Check, Ellipsis, type LucideIcon } from 'lucide-react';
import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import type { ReactElement } from 'react';

import { Button } from '@platform/ui/components/button';
import { cn } from '@platform/ui/lib/utils';

export interface MenuAction {
    label: string;
    onClick: () => void;
    tone?: 'default' | 'danger';
    disabled?: boolean;
}

/** An item that is on or off rather than performed — a column a table may show. */
export interface MenuToggle {
    label: string;
    checked: boolean;
    onCheckedChange: () => void;
    disabled?: boolean;
}

export type MenuItem = MenuAction | MenuToggle;

function isToggle(item: MenuItem): item is MenuToggle {
    return 'checked' in item;
}

/**
 * The "···" menu: the actions that do not earn a button of their own (docs/11
 * §2). In an action bar the trigger is a button among buttons — 44px, with the
 * same edge; in a row it is a quiet 36px icon. The menu moves with the arrow
 * keys, closes on Escape and returns focus to the trigger.
 *
 * A menu whose items are states rather than actions — the columns a table may
 * show (docs/11 §4) — passes toggles and its own `icon`, because "···" means
 * "more actions" and a list of ticks is not that. A menu that opens from a
 * named place rather than from "···" — a shop's "Hesabım" (docs/14 §7.1) —
 * passes its own `trigger`, a button that names itself. One menu either way,
 * so the chrome, the keyboard path and the edges cannot drift apart.
 */
export function ActionMenu({
    items,
    label,
    size = 'md',
    icon: Icon = Ellipsis,
    trigger,
}: {
    items: MenuItem[];
    label: string;
    size?: 'md' | 'sm';
    icon?: LucideIcon;
    /** A button of the caller's own to open the menu from, in place of "···". */
    trigger?: ReactElement;
}) {
    const item = cn(
        'flex min-h-9 cursor-pointer items-center rounded-control px-3 py-2 text-13 outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-55 pointer-coarse:min-h-11',
    );
    return (
        <MenuPrimitive.Root>
            <MenuPrimitive.Trigger asChild>
                {trigger ?? (
                    <Button variant={size === 'sm' ? 'ghost' : 'secondary'} size={size === 'sm' ? 'icon-sm' : 'icon'} aria-label={label}>
                        <Icon aria-hidden="true" />
                    </Button>
                )}
            </MenuPrimitive.Trigger>
            <MenuPrimitive.Portal>
                <MenuPrimitive.Content
                    align="end"
                    sideOffset={4}
                    className="z-50 min-w-44 overflow-hidden rounded-card border border-border bg-popover p-1 text-popover-foreground shadow-popover data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
                >
                    {items.map((entry) =>
                        isToggle(entry) ? (
                            <MenuPrimitive.CheckboxItem
                                key={entry.label}
                                checked={entry.checked}
                                disabled={entry.disabled}
                                // Kept open: turning three columns on is three
                                // presses, and a menu that closes on each is three
                                // round trips through the trigger.
                                onSelect={(event) => event.preventDefault()}
                                onCheckedChange={entry.onCheckedChange}
                                className={cn(item, 'gap-2 text-foreground data-highlighted:bg-accent')}
                            >
                                <span className="flex size-4 flex-none items-center justify-center">
                                    <MenuPrimitive.ItemIndicator>
                                        <Check aria-hidden="true" className="size-4" />
                                    </MenuPrimitive.ItemIndicator>
                                </span>
                                {entry.label}
                            </MenuPrimitive.CheckboxItem>
                        ) : (
                            <MenuPrimitive.Item
                                key={entry.label}
                                disabled={entry.disabled}
                                onSelect={entry.onClick}
                                className={cn(
                                    item,
                                    entry.tone === 'danger'
                                        ? 'text-destructive-strong data-highlighted:bg-destructive-subtle'
                                        : 'text-foreground data-highlighted:bg-accent',
                                )}
                            >
                                {entry.label}
                            </MenuPrimitive.Item>
                        ),
                    )}
                </MenuPrimitive.Content>
            </MenuPrimitive.Portal>
        </MenuPrimitive.Root>
    );
}
